import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";

export default function CommissionDuesBanner({ onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/me/commission-dues").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const sid = params.get("commission_session");
    if (!sid) return;
    api.get(`/commission-dues/status/${sid}`).then(({ data }) => {
      if (data.paid) { toast.success("Commissioni saldate: profilo attivo"); load(); onChanged?.(); }
      else toast.error("Pagamento non confermato");
    }).catch(() => {}).finally(() => { params.delete("commission_session"); setParams(params, { replace: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/commission-dues/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  if (!data || data.open_count === 0) return null;
  const blocked = data.blocked;
  const open = data.items.filter(i => ["due", "overdue"].includes(i.status));
  const fmt = (d) => d ? new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }) : "";
  return (
    <div className={`mt-4 rounded-2xl border p-4 ${blocked ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`} data-testid="commission-dues-banner">
      <div className="flex items-start gap-3">
        <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${blocked ? "text-red-700" : "text-amber-700"}`} />
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-semibold ${blocked ? "text-red-900" : "text-amber-900"}`} data-testid="commission-dues-title">
            {blocked ? "Profilo sospeso: commissioni scadute" : `Commissioni da saldare · € ${data.open_total.toFixed(2)}`}
          </div>
          <div className={`text-xs mt-0.5 ${blocked ? "text-red-800" : "text-amber-800"}`}>
            {blocked
              ? `Hai € ${data.open_total.toFixed(2)} di commissioni scadute su ordini pagati con bonifico diretto. Non puoi fare offerte né ricevere ordini finché non saldi: lo sblocco è immediato.`
              : `Su ordini pagati con bonifico diretto. Scadenza ${fmt(data.next_due_at)}: dopo, il profilo viene sospeso. Se incassi un ordine con carta/PayPal la tratteniamo noi dal payout.`}
          </div>
          <ul className="mt-2 space-y-0.5 text-[11px] font-mono text-slate-700">
            {open.map(i => <li key={i.id} className="flex justify-between gap-3" data-testid={`commission-due-${i.id}`}><span className="truncate">{i.order_title} · scade {fmt(i.due_at)}</span><span>€ {i.remaining.toFixed(2)}</span></li>)}
          </ul>
        </div>
        <Button onClick={pay} disabled={busy} className={`rounded-full shrink-0 ${blocked ? "bg-red-700 hover:bg-red-800 text-white" : "ga-btn-primary"}`} data-testid="commission-pay-button">
          <CreditCard className="w-4 h-4 mr-2" />{busy ? "Apertura..." : `Salda ora € ${data.open_total.toFixed(2)}`}
        </Button>
      </div>
      {!blocked && <div className="mt-2 flex items-center gap-1 text-[11px] text-emerald-800"><CheckCircle2 className="w-3 h-3" /> Pagamento con carta, verificato automaticamente: nessuna ricevuta da inviare.</div>}
    </div>
  );
}
